import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiSave, FiX, FiVideo, FiDownload, FiTrash2 } from 'react-icons/fi';
import { interviewsAPI, studentsAPI, settingsAPI, zoomAPI, usersAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { getTodayDateInputMin, isDateTimeLocalInFuture, storedDatetimeToLocalInput } from '../../utils/futureDateTime';
import { resolveDefaultInterviewTimezone } from '../../utils/browserTimezone';
import {
  formatInterviewSlotAvailabilitySuffix,
  canBypassInterviewSlotCapacity
} from '../../utils/interviewSlotAvailabilityLabel';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import {
  getDocumentFileSizeError,
  getInterviewReportFileError,
  INTERVIEW_REPORT_ACCEPT
} from '../../utils/uploadLimits';
import AuditTimeline from '../../components/Common/AuditTimeline';
import { useTranslation } from 'react-i18next';

const INTERVIEW_OUTCOMES = ['pending', 'pass', 'fail', 'student_did_not_join'];

const formatOutcomeLabel = (outcome, t) => {
  const translated = t(`interviews.outcomes.${outcome}`, { defaultValue: '' });
  if (translated) return translated;
  return String(outcome)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const InterviewForm = () => {
  const { t } = useTranslation();
  const { user, canPerformAction } = useAuth();
  const { getConfig, activeCountry } = useCountry();
  const config = getConfig();
  const { id, studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isStudentContextLocked = Boolean(studentId || location.state?.fromStudentId);
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [zoomConfigured, setZoomConfigured] = useState(false);
  const [zoomGenerating, setZoomGenerating] = useState(false);
  const [initialScheduledAt, setInitialScheduledAt] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [slotDate, setSlotDate] = useState('');
  const [slotStartTime, setSlotStartTime] = useState('');
  const [slotOptions, setSlotOptions] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [zoomHosts, setZoomHosts] = useState([]);
  const [reportFile, setReportFile] = useState(null);
  const reportFileInputRef = useRef(null);
  const clearReportFileSelection = useCallback(() => {
    setReportFile(null);
    if (reportFileInputRef.current) reportFileInputRef.current.value = '';
  }, []);
  const [hasServerReport, setHasServerReport] = useState(false);
  const [serverReportName, setServerReportName] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const showInterviewMode = config.features.meetingRequired === false;
  const [form, setForm] = useState({
    student_id: studentId || '',
    scheduled_at: '',
    timezone: config.defaultTimezone,
    interview_link: '',
    interview_mode: '',
    remarks: '',
    interview_comment: '',
    outcome: 'pending',
    interview_host_user_id: '',
    zoom_email_used: '',
    zoom_meeting_id: '',
    zoom_join_url: '',
    zoom_start_url: '',
    zoom_passcode: ''
  });

  /** Resolved via API — student list from getAll() is paginated (default 25), so selection may not include full profile. */
  const [resolvedStudentLanguage, setResolvedStudentLanguage] = useState(null);

  useEffect(() => {
    if (!form.student_id) {
      setResolvedStudentLanguage(null);
      return;
    }
    let cancelled = false;
    studentsAPI
      .getById(form.student_id)
      .then((res) => {
        if (cancelled) return;
        const lang = res.data?.language;
        setResolvedStudentLanguage(lang === 'FRENCH' ? 'FRENCH' : 'ENGLISH');
      })
      .catch(() => {
        if (!cancelled) setResolvedStudentLanguage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.student_id]);

  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const needsInterviewHost = isFranceCRM && resolvedStudentLanguage === 'FRENCH';
  const selectedStudent = students.find((s) => String(s.student_id) === String(form.student_id));
  const selectedCampusId = selectedStudent?.campus_id || null;

  const scheduledAtFromSlot = (dateStr, hhmm) => {
    if (!dateStr || !hhmm) return '';
    return `${String(dateStr).slice(0, 10)}T${String(hhmm).slice(0, 5)}`;
  };

  const getReturnPath = () => {
    const fromStudentId = location.state?.fromStudentId;
    if (fromStudentId) return `/students/${fromStudentId}`;
    if (studentId) return `/students/${studentId}`;
    if (form.student_id) return `/students/${form.student_id}`;
    return '/interviews';
  };

  useEffect(() => {
    studentsAPI
      .getAll({ page: 1, limit: 5000 })
      .then((r) => {
        const studentsData = r.data.students || r.data || [];
        setStudents(studentsData);
      })
      .catch(() => {});
    settingsAPI.getInterviewTimezones().then((res) => {
      const activeZones = (res.data || []).filter((tz) => tz.status === 'active');
      setTimezones(activeZones);
      if (activeZones.length > 0 && !id) {
        setForm((prev) => ({ ...prev, timezone: resolveDefaultInterviewTimezone(activeZones, config.defaultTimezone) }));
      }
    }).catch(() => {});

    zoomAPI.getStatus().then(r => setZoomConfigured(r.data.configured)).catch(() => {});

    if (id) {
      setLoading(true);
      interviewsAPI.getById(id)
        .then(res => {
          const interview = res.data;
          const tz = interview.timezone || resolveDefaultInterviewTimezone(timezones, config.defaultTimezone);
          const scheduledDateTime = interview.scheduled_at ? storedDatetimeToLocalInput(interview.scheduled_at) : '';
          const initDate = scheduledDateTime ? scheduledDateTime.slice(0, 10) : '';
          const initTime = scheduledDateTime ? scheduledDateTime.slice(11, 16) : '';
          
          setForm({
            student_id: interview.student_id.toString(),
            scheduled_at: scheduledDateTime,
            timezone: tz,
            interview_link: interview.interview_link || '',
            interview_mode: interview.interview_mode || '',
            remarks: interview.remarks || '',
            interview_comment: interview.interview_comment || '',
            outcome: interview.outcome || 'pending',
            interview_host_user_id:
              interview.interview_host_user_id != null ? String(interview.interview_host_user_id) : '',
            zoom_email_used: interview.zoom_email_used || '',
            zoom_meeting_id: interview.zoom_meeting_id || '',
            zoom_join_url: interview.zoom_join_url || '',
            zoom_start_url: interview.zoom_start_url || '',
            zoom_passcode: interview.zoom_passcode || ''
          });
          clearReportFileSelection();
          setHasServerReport(Boolean(interview.report_file_path));
          setServerReportName(interview.report_file_name || '');
          setSlotDate(initDate);
          setSlotStartTime(initTime);
          setInitialScheduledAt(scheduledDateTime);
        })
        .catch(err => {
          toast.error(t('interviews.failedLoadSingle'));
          navigate(getReturnPath());
        })
        .finally(() => setLoading(false));
    } else {
      setInitialScheduledAt(null);
      const today = getTodayDateInputMin();
      setSlotDate(today);
      setSlotStartTime('');
    }
  }, [id, studentId, navigate, clearReportFileSelection, config.defaultTimezone]);

  useEffect(() => {
    if (!needsInterviewHost) {
      setZoomHosts([]);
      return;
    }
    usersAPI.getInterviewZoomHosts().then((r) => setZoomHosts(r.data || [])).catch(() => setZoomHosts([]));
  }, [needsInterviewHost]);

  useEffect(() => {
    if (!needsInterviewHost) {
      setForm((f) => {
        if (!f.interview_host_user_id && !f.zoom_email_used) return f;
        return { ...f, interview_host_user_id: '', zoom_email_used: '' };
      });
    }
  }, [needsInterviewHost]);

  useEffect(() => {
    if (!slotDate) {
      setSlotOptions([]);
      return;
    }
    setSlotsLoading(true);
    settingsAPI
      .getInterviewSlotAvailability({ date: slotDate, type: 'interview', campus_id: selectedCampusId || undefined })
      .then((res) => setSlotOptions(res.data || []))
      .catch(() => setSlotOptions([]))
      .finally(() => setSlotsLoading(false));
  }, [slotDate, selectedCampusId]);

  useEffect(() => {
    const next = scheduledAtFromSlot(slotDate, slotStartTime);
    setForm((f) => ({ ...f, scheduled_at: next }));
  }, [slotDate, slotStartTime]);

  useEffect(() => {
    if (!id) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    settingsAPI
      .getAuditLog({ entity_type: 'interview', entity_id: id, limit: 200, page: 1 })
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [id]);

  const openInterviewReport = async (interviewId) => {
    try {
      const { data } = await interviewsAPI.getReportUrl(interviewId);
      if (data?.url) window.open(data.url, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.message || t('interviews.couldNotOpenReport'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.student_id || !form.scheduled_at || !form.timezone) {
      toast.error(t('interviews.fillRequired'));
      return;
    }
    const slotChanged = !id || form.scheduled_at !== initialScheduledAt;
    if (slotChanged && !isDateTimeLocalInFuture(form.scheduled_at)) {
      toast.error(t('interviews.futureDateRequired'));
      return;
    }

    const reportErr = getInterviewReportFileError(reportFile);
    if (reportErr) {
      toast.error(reportErr);
      return;
    }
    if (needsInterviewHost && !form.interview_host_user_id) {
      toast.error(t('interviews.frenchHostRequired'));
      return;
    }

    setLoading(true);
    try {
      const interviewData = {
        scheduled_at: form.scheduled_at,
        timezone: form.timezone,
        interview_link: form.interview_link || null,
        interview_mode: form.interview_mode || null,
        remarks: form.remarks || null,
        zoom_meeting_id: form.zoom_meeting_id || null,
        zoom_join_url: form.zoom_join_url || null,
        zoom_start_url: form.zoom_start_url || null,
        zoom_passcode: form.zoom_passcode || null,
        interview_host_user_id: needsInterviewHost ? form.interview_host_user_id || null : null,
        zoom_email_used: needsInterviewHost ? form.zoom_email_used || null : null
      };

      if (id) {
        interviewData.outcome = form.outcome;
        interviewData.interview_comment = form.interview_comment || null;
      }

      let savedInterviewId = id ? Number(id) : null;

      if (id) {
        await interviewsAPI.update(id, interviewData);
      } else {
        const { data } = await interviewsAPI.create(form.student_id, interviewData);
        savedInterviewId = data?.interview_id ?? null;
      }

      if (reportFile && savedInterviewId) {
        const fd = new FormData();
        fd.append('file', reportFile);
        try {
          await interviewsAPI.uploadReport(savedInterviewId, fd);
          toast.success(
            id ? t('interviews.updatedReportUploadedSuccess') : t('interviews.scheduledReportUploadedSuccess')
          );
        } catch (upErr) {
          toast.error(upErr.response?.data?.message || t('interviews.savedReportFailed'));
        }
      } else {
        toast.success(id ? t('interviews.updatedSuccess') : t('interviews.scheduledSuccess'));
      }

      navigate(getReturnPath());
    } catch (err) {
      toast.error(err.response?.data?.message || t('interviews.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>{id ? t('interviews.editTitle') : t('interviews.scheduleTitle')}</h1>
          <p>{id ? t('interviews.editSubtitle') : t('interviews.scheduleSubtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={() => navigate(getReturnPath())}>
            <FiX /> {t('common.cancel')}
          </button>
        </div>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('interviews.studentLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <select
                value={form.student_id}
                onChange={(e) => setForm({ ...form, student_id: e.target.value })}
                disabled={!!id || isStudentContextLocked}
                required
              >
                <option value="">{t('interviews.selectStudent')}</option>
                {students.map(s => (
                  <option key={s.student_id} value={s.student_id}>
                    {s.student_ref} - {s.title} {s.first_name} {s.last_name}
                  </option>
                ))}
              </select>
              {form.student_id && resolvedStudentLanguage && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  {t('studentForm.languageLabel')}:{' '}
                  {resolvedStudentLanguage === 'FRENCH'
                    ? t('studentForm.languageFrench')
                    : t('studentForm.languageEnglish')}
                </p>
              )}
            </div>

            {needsInterviewHost && (
              <>
                <div className="form-group">
                  <label>
                    {t('interviews.interviewHostLabel')}{' '}
                    <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <select
                    value={form.interview_host_user_id}
                    onChange={(e) => {
                      const v = e.target.value;
                      const h = zoomHosts.find((x) => String(x.user_id) === String(v));
                      setForm((f) => ({
                        ...f,
                        interview_host_user_id: v,
                        zoom_email_used: h?.zoom_meeting_email || ''
                      }));
                    }}
                    required
                  >
                    <option value="">{t('interviews.selectInterviewHost')}</option>
                    {form.interview_host_user_id &&
                      !zoomHosts.some((h) => String(h.user_id) === String(form.interview_host_user_id)) && (
                        <option value={form.interview_host_user_id}>
                          {form.zoom_email_used || `#${form.interview_host_user_id}`}
                        </option>
                      )}
                    {zoomHosts.map((h) => (
                      <option key={h.user_id} value={h.user_id}>
                        {h.name} — {h.zoom_meeting_email}
                      </option>
                    ))}
                  </select>
                  {zoomHosts.length === 0 && (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                      {t('interviews.noZoomHostsConfigured')}
                    </p>
                  )}
                </div>
                {form.interview_host_user_id && form.zoom_email_used && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <p style={{ fontSize: 13, margin: 0, color: 'var(--text-secondary)' }}>
                      <strong>{t('interviews.hostZoomAccount')}</strong> {form.zoom_email_used}
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label>{t('interviews.dateLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <input
                type="date"
                value={slotDate}
                min={getTodayDateInputMin()}
                onChange={(e) => {
                  setSlotDate(e.target.value);
                  setSlotStartTime('');
                }}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('interviews.slotLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <select
                value={slotStartTime}
                onChange={(e) => setSlotStartTime(e.target.value)}
                required
                disabled={!slotDate || slotsLoading}
              >
                <option value="">
                  {slotsLoading ? t('studentDetail.modals.loadingSlots') : (slotOptions.length ? t('studentDetail.modals.selectSlot') : t('studentDetail.modals.noSlotsConfigured'))}
                </option>
                {slotOptions.map((s) => {
                  const value = String(s.start_time || '').slice(0, 5);
                  const label = s.label || `${value} - ${String(s.end_time || '').slice(0, 5)}`;
                  const isFull = !!s.is_full;
                  const bypass = canBypassInterviewSlotCapacity(user);
                  const disabled = !bypass && isFull;
                  const capText = formatInterviewSlotAvailabilitySuffix(s);
                  return (
                    <option key={s.slot_id} value={value} disabled={disabled}>
                      {label} — {capText}{disabled ? ` ${t('studentDetail.modals.full')}` : ''}
                    </option>
                  );
                })}
              </select>
              {canBypassInterviewSlotCapacity(user) && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  {t('interviews.adminCapacity')}
                </p>
              )}
            </div>

            <div className="form-group">
              <label>{t('interviews.timezoneLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <select
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                required
              >
                {timezones.length > 0 ? (
                  timezones.map((tz) => (
                    <option key={tz.timezone_id} value={tz.timezone_name}>
                      {tz.timezone_label || tz.timezone_name}
                    </option>
                  ))
                ) : (
                  <option value={config.defaultTimezone}>{config.defaultTimezone} (default)</option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label>{t('interviews.interviewLink')}</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="url"
                  value={form.interview_link}
                  onChange={(e) => setForm({ ...form, interview_link: e.target.value })}
                  placeholder={t('interviews.linkPlaceholder')}
                  style={{ flex: 1 }}
                />
                {zoomConfigured && form.scheduled_at && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                    disabled={zoomGenerating || (needsInterviewHost && !form.interview_host_user_id)}
                    onClick={async () => {
                      if (needsInterviewHost && !form.interview_host_user_id) {
                        toast.error(t('interviews.frenchHostRequired'));
                        return;
                      }
                      setZoomGenerating(true);
                      try {
                        const { data } = await zoomAPI.generate({
                          topic: `Interview - ${students.find(s => String(s.student_id) === String(form.student_id))?.first_name || 'Student'}`,
                          start_time: form.scheduled_at,
                          duration: 60,
                          timezone: form.timezone || resolveDefaultInterviewTimezone(timezones, config.defaultTimezone),
                          entity_type: 'interview',
                          entity_id: id || undefined,
                          host_user_id: needsInterviewHost ? form.interview_host_user_id : undefined
                        });
                        setForm((f) => ({
                          ...f,
                          interview_link: data.join_url,
                          zoom_meeting_id: data.zoom_meeting_id,
                          zoom_join_url: data.join_url,
                          zoom_start_url: data.start_url,
                          zoom_passcode: data.passcode,
                          zoom_email_used: data.zoom_email_used || f.zoom_email_used,
                          interview_host_user_id:
                            data.interview_host_user_id != null
                              ? String(data.interview_host_user_id)
                              : f.interview_host_user_id
                        }));
                        toast.success(t('interviews.zoomGenerated'));
                      } catch (err) {
                        toast.error(err.response?.data?.message || t('interviews.zoomFailed'));
                      }
                      setZoomGenerating(false);
                    }}
                  >
                    <FiVideo size={14} /> {zoomGenerating ? t('interviews.creatingZoom') : t('interviews.generateZoom')}
                  </button>
                )}
              </div>
            </div>

            {showInterviewMode && (
              <div className="form-group">
                <label>{t('interviews.interviewMode')}</label>
                <select
                  value={form.interview_mode}
                  onChange={(e) => setForm({ ...form, interview_mode: e.target.value })}
                >
                  <option value="">{t('interviews.selectMode')}</option>
                  <option value="online">{t('common.online')}</option>
                  <option value="offline">{t('common.offline')}</option>
                </select>
              </div>
            )}

            {id && (
              <div className="form-group">
                <label>{t('interviews.outcomeLabel')}</label>
                <select
                  value={form.outcome}
                  onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                >
                  {INTERVIEW_OUTCOMES.map(o => (
                    <option key={o} value={o}>{formatOutcomeLabel(o, t)}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>{t('interviews.remarksLabel')}</label>
              <textarea
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                rows={6}
                placeholder={t('interviews.remarksPlaceholder')}
              />
            </div>

            {id && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>{t('interviews.interviewComment')}</label>
                <textarea
                  value={form.interview_comment}
                  onChange={(e) => setForm({ ...form, interview_comment: e.target.value })}
                  rows={4}
                  placeholder={t('interviews.commentPlaceholder')}
                />
              </div>
            )}

            {id && canPerformAction('interviews', 'read') && (hasServerReport || reportFile) && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>{t('interviews.interviewReport')}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  {reportFile ? (
                    <span style={{ fontSize: 14 }}>{t('interviews.newFile')} {reportFile.name}</span>
                  ) : hasServerReport ? (
                    <>
                      <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{serverReportName || t('interviews.uploadedReport')}</span>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => openInterviewReport(id)}
                      >
                        <FiDownload size={14} /> {t('common.open')}
                      </button>
                      {canPerformAction('interviews', 'update') && (
                        <button
                          type="button"
                          className="btn btn-outline btn-sm btn-danger"
                          disabled={reportBusy}
                          onClick={async () => {
                            if (!window.confirm(t('interviews.removeReportStorageConfirm'))) return;
                            setReportBusy(true);
                            try {
                              await interviewsAPI.deleteReport(id);
                              setHasServerReport(false);
                              setServerReportName('');
                              clearReportFileSelection();
                              toast.success(t('interviews.reportRemoved'));
                            } catch (err) {
                              toast.error(err.response?.data?.message || t('interviews.reportRemoveFailed'));
                            } finally {
                              setReportBusy(false);
                            }
                          }}
                        >
                          <FiTrash2 size={14} /> {t('interviews.removeReport')}
                        </button>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {id && canPerformAction('interviews', 'update') && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>{t('interviews.replaceReport')}</label>
                <input
                  ref={reportFileInputRef}
                  type="file"
                  accept={INTERVIEW_REPORT_ACCEPT}
                  onChange={(e) => {
                    const inputEl = e.target;
                    const f = inputEl.files?.[0];
                    if (!f) return;
                    const err = getInterviewReportFileError(f);
                    if (err) {
                      toast.error(err);
                      inputEl.value = '';
                      return;
                    }
                    setReportFile(f);
                  }}
                />
                {reportFile && (
                  <div style={{ marginTop: 8, fontSize: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span>
                      {t('interviews.selectedFile')} <strong>{reportFile.name}</strong>
                    </span>
                    <button type="button" className="btn btn-outline btn-sm" onClick={clearReportFileSelection}>
                      {t('common.clearFile')}
                    </button>
                  </div>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  {t('interviews.storedInCloud')}
                </p>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
            <button 
              type="button" 
              className="btn btn-outline" 
              onClick={() => navigate(getReturnPath())}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <FiSave /> {loading ? t('interviews.savingBtn') : (id ? t('interviews.updateBtn') : t('interviews.scheduleBtn'))}
            </button>
          </div>
        </form>
      </div>

      {id && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 12 }}>
          <h3 style={{ margin: '16px 20px 12px', fontWeight: 800 }}>{t('common.history')}</h3>
          <div style={{ padding: '0 0 12px' }}>
            {historyLoading ? (
              <div className="loading-spinner"><div className="spinner" /></div>
            ) : (
              <AuditTimeline
                logs={history}
                showEntity={false}
                filterLog={(log) => {
                  const v = (x) => {
                    try {
                      const o = typeof x === 'string' ? JSON.parse(x) : x;
                      return o && typeof o === 'object' ? o : {};
                    } catch {
                      return {};
                    }
                  };
                  const oldObj = v(log.old_values);
                  const newObj = v(log.new_values);
                  const t = newObj.interview_type ?? oldObj.interview_type;
                  return !t || t === 'regular';
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewForm;
